import React, { useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { ModalService } from 'ui-components/services/modal-service';

import { GenericErrorRetryContainer } from '../../../containers/generic-error-retry-container';
import { deleteTagSuccess } from '../../../store/tags/tags.actions';
import { deleteTag } from '../../../store/tags/tags.api';
import { messages } from './messages';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';
import { EDIT } from './constants';

export const useManageTags = ({ openEditTag, source }) => {
  const dispatch = useDispatch();
  const [editTag, setEditTag] = useAsyncSafeState(undefined);
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const trackTagManagementWorkflow = useTrackWorkflow(Workflows.TAG_MANAGEMENT_FLOW);

  const onEditClicked = useCallback(
    (tag) => {
      setEditTag(tag);
      openEditTag();
      trackTagManagementWorkflow(WorkflowSteps.START, {
        modalType: EDIT,
        source,
      });
    },
    [setEditTag, openEditTag, trackTagManagementWorkflow, source],
  );

  const onDeleteClicked = useCallback(
    async (tag) => {
      const { tagId } = tag;
      setIsLoading(true);
      try {
        await deleteTag(tagId);
        dispatch(deleteTagSuccess({ tagId }));
      } catch {
        ModalService.launchModal({
          component: GenericErrorRetryContainer,
          props: {
            onClick: () => onDeleteClicked(tag),
            title: <FormattedMessage {...messages.deleteTagErrorTitle} />,
            message: <FormattedMessage {...messages.deleteTagErrorMessage} />,
          },
        });
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, setIsLoading],
  );

  const memoizedAddTagsObj = useMemo(() => {
    return {
      editTag,
      onEditClicked,
      isLoading,
      onDeleteClicked,
    };
  }, [editTag, onEditClicked, isLoading, onDeleteClicked]);

  return memoizedAddTagsObj;
};
